import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVault } from '../../contexts/VaultContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import PasswordList from '../../components/vault/PasswordList';
import CardDetailsList from '../../components/cardDetails/CardDetailsList';
import BankDetailsList from '../../components/bankDetails/BankDetailsList';
import PersonalDetailsList from '../../components/personalDetails/PersonalDetailsList';
import { IDecryptedPasswordEntry } from '../../types/vault.types';
import { CardDetail } from '../../types/cardDetails';
import { PersonalDetail } from '../../types/personalDetails';
import { BankDetail } from '../../types/bankDetails';
import { VaultService } from '../../services/vault.service';
import CardDetailsService from '../../services/cardDetails.service';
import PersonalDetailsService from '../../services/personalDetails.service';
import BankDetailsService from '../../services/bankDetails.service';
import CardDetailsModal from '../../components/cardDetails/CardDetailsModal';
import BankDetailsModal from '../../components/bankDetails/BankDetailsModal';
import PersonalDetailsModal from '../../components/personalDetails/PersonalDetailsModal';
import PasswordModal from '../../components/vault/PasswordModal';
import {
    PlusIcon,
    AdjustmentsHorizontalIcon,
    ArrowsUpDownIcon,
    ChevronDownIcon,
    XMarkIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';
import AddItemModal from '../../components/vault/AddItemModal';
import { toast } from 'react-hot-toast';
import { useAppSelector } from '../../hooks/useRedux';
import RSAMigrationModalTrigger from '../../components/auth/RSAMigrationModalTrigger';

// Tab type definition
type TabType = 'passwords' | 'cardDetails' | 'bankDetails' | 'personalDetails';

// Sort options
type SortOption = 'newest' | 'alphabetical' | 'passwordStrength';

// Filter options for password strength
type PasswordStrengthFilter = 'all' | 'veryWeak' | 'weak' | 'fair' | 'strong' | 'veryStrong';

const VaultPage: React.FC = () => {
    const navigate = useNavigate();
    const { searchQuery, entries: contextEntries, loading: contextLoading, refreshEntries } = useVault();
    const { activeWorkspace } = useWorkspace();

    // State variables for all items
    const [entries, setEntries] = useState<IDecryptedPasswordEntry[]>([]);
    const [cardDetails, setCardDetails] = useState<CardDetail[]>([]);
    const [personalDetails, setPersonalDetails] = useState<PersonalDetail[]>([]);
    const [bankDetails, setBankDetails] = useState<BankDetail[]>([]);

    // Tab state
    const [activeTab, setActiveTab] = useState<TabType>('passwords');

    // Sorting and filtering states
    const [sortOption, setSortOption] = useState<SortOption>('newest');
    const [passwordStrengthFilter, setPasswordStrengthFilter] = useState<PasswordStrengthFilter>('all');
    const [showSharedOnly, setShowSharedOnly] = useState<boolean>(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
    const [tempPasswordStrengthFilter, setTempPasswordStrengthFilter] = useState<PasswordStrengthFilter>('all');
    const [tempShowSharedOnly, setTempShowSharedOnly] = useState<boolean>(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const filterMenuRef = useRef<HTMLDivElement>(null);

    // Loading states for each tab
    const [loadingPasswords, setLoadingPasswords] = useState<boolean>(contextLoading);
    const [loadingCards, setLoadingCards] = useState<boolean>(false);
    const [loadingBanks, setLoadingBanks] = useState<boolean>(false);
    const [loadingPersonals, setLoadingPersonals] = useState<boolean>(false);

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [isBankModalOpen, setIsBankModalOpen] = useState(false);
    const [isPersonalModalOpen, setIsPersonalModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<CardDetail | null>(null);
    const [selectedBank, setSelectedBank] = useState<BankDetail | null>(null);
    const [selectedPersonal, setSelectedPersonal] = useState<PersonalDetail | null>(null);
    const { team_id } = useAppSelector(state => state.auth);
    const workspaceKey = activeWorkspace?.id || team_id || undefined;

    const [itemCounts, setItemCounts] = useState({
        passwords: 0,
        cards: 0,
        banks: 0,
        personals: 0
    });

    const refreshItemCounts = useCallback(async () => {
        try {
            const counts = await VaultService.getItemCounts(workspaceKey);
            setItemCounts(counts);
        } catch (error) {
            console.error('Error fetching vault counts:', error);
        }
    }, [workspaceKey]);

    // Add this useEffect to refresh entries when component mounts
    useEffect(() => {
        if (activeTab === 'passwords') {
            refreshEntries();
        }
    }, [activeTab, refreshEntries]);

    // Update entries useEffect to include refreshEntries in dependencies
    useEffect(() => {
        if (!contextLoading) {
            setEntries(contextEntries);
        }
        setLoadingPasswords(contextLoading);
    }, [contextEntries, contextLoading, refreshEntries]);

    useEffect(() => {
        refreshItemCounts();
    }, [refreshItemCounts]);

    // Fetch card details data
    const fetchCardDetailsData = async () => {
        setLoadingCards(true);
        try {
            const cardsData = await CardDetailsService.getAllCardDetails(workspaceKey);
            setCardDetails(cardsData);
            await refreshItemCounts();
        } catch (error) {
            console.error('Error fetching card details:', error);
        } finally {
            setLoadingCards(false);
        }
    };

    // Fetch bank details data
    const fetchBankDetailsData = async () => {
        setLoadingBanks(true);
        try {
            const banksData = await BankDetailsService.getAllBankDetails(workspaceKey);
            setBankDetails(banksData);
            await refreshItemCounts();
        } catch (error) {
            console.error('Error fetching bank details:', error);
        } finally {
            setLoadingBanks(false);
        }
    };

    // Fetch personal details data
    const fetchPersonalDetailsData = async () => {
        setLoadingPersonals(true);
        try {
            const personalsData = await PersonalDetailsService.getAllPersonalDetails(workspaceKey);
            setPersonalDetails(personalsData);
            await refreshItemCounts();
        } catch (error) {
            console.error('Error fetching personal details:', error);
        } finally {
            setLoadingPersonals(false);
        }
    };

    // Handle tab change
    const handleTabChange = async (tab: TabType) => {
        setActiveTab(tab);
        await refreshItemCounts();

        // Load data for each tab type
        switch (tab) {
            case 'passwords':
                await refreshEntries(); // Explicitly refresh passwords when switching to passwords tab
                break;
            case 'cardDetails':
                if (cardDetails.length === 0) fetchCardDetailsData();
                break;
            case 'bankDetails':
                if (bankDetails.length === 0) fetchBankDetailsData();
                break;
            case 'personalDetails':
                if (personalDetails.length === 0) fetchPersonalDetailsData();
                break;
        }
    };

    // Filter items based on search query
    const filteredEntries = entries.filter(entry => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            entry.title?.toLowerCase().includes(query) ||
            entry.username?.toLowerCase().includes(query) ||
            entry.website_url?.toLowerCase().includes(query)
        );
    });

    const filteredCardDetails = cardDetails.filter(detail => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            detail.name?.toLowerCase().includes(query) ||
            detail.encrypted_card_holder_name?.toLowerCase().includes(query)
        );
    });

    const filteredBankDetails = bankDetails.filter(detail => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            detail.name?.toLowerCase().includes(query) ||
            detail.bank_name?.toLowerCase().includes(query)
        );
    });

    const filteredPersonalDetails = personalDetails.filter(detail => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            detail.name?.toLowerCase().includes(query) ||
            detail.encrypted_first_name?.toLowerCase().includes(query) ||
            detail.encrypted_last_name?.toLowerCase().includes(query) ||
            detail.encrypted_email_address?.toLowerCase().includes(query)
        );
    });

    const handleItemSelect = (itemType: string) => {
        if (itemType === 'password') {
            setIsModalOpen(true);
        } else if (itemType === 'personalDetails') {
            setSelectedPersonal(null);
            setIsPersonalModalOpen(true);
        } else if (itemType === 'cardDetails') {
            setSelectedCard(null);
            setIsCardModalOpen(true);
        } else if (itemType === 'bankDetails') {
            setSelectedBank(null);
            setIsBankModalOpen(true);
        }
        setIsItemModalOpen(false);
    };

    const handleSuccess = async () => {
        setIsModalOpen(false);
        toast.success('Item added successfully');
        await refreshEntries();
        await refreshItemCounts();
    };

    // Get the filtered and sorted data for the current tab
    // const getFilteredAndSortedData = () => {
    //     switch (activeTab) {
    //         case 'passwords':
    //             return getFilteredAndSortedEntries();
    //         case 'cardDetails':
    //             return getFilteredAndSortedCards();
    //         case 'bankDetails':
    //             return getFilteredAndSortedBanks();
    //         case 'personalDetails':
    //             return getFilteredAndSortedPersonals();
    //         default:
    //             return [];
    //     }
    // };

    // Filter password entries based on selected filters
    const getFilteredAndSortedEntries = (): IDecryptedPasswordEntry[] => {
        let filtered = [...filteredEntries];

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(entry => !entry.is_owner);
        }

        // Apply password strength filter
        if (passwordStrengthFilter !== 'all') {
            const strengthFilterMap: Record<PasswordStrengthFilter, string> = {
                'all': '',
                'veryWeak': 'Very Weak',
                'weak': 'Weak',
                'fair': 'Fair',
                'strong': 'Strong',
                'veryStrong': 'Very Strong'
            };

            filtered = filtered.filter(entry =>
                entry.password_strength === strengthFilterMap[passwordStrengthFilter]
            );
        }

        // Apply sorting
        return getSortedPasswordEntries(filtered);
    };

    // Sort password entries based on selected sort option
    const getSortedPasswordEntries = (entries: IDecryptedPasswordEntry[]): IDecryptedPasswordEntry[] => {
        if (!entries.length) return entries;
        let sorted = [...entries];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
            case 'alphabetical':
                return sorted.sort((a, b) => a.title.localeCompare(b.title));
            case 'passwordStrength':
                // Map strength to numeric value for sorting
                const strengthMap: Record<string, number> = {
                    'Very Weak': 1,
                    'Weak': 2,
                    'Fair': 3,
                    'Strong': 4,
                    'Very Strong': 5
                };
                return sorted.sort((a, b) => {
                    const strengthA = strengthMap[a.password_strength] || 0;
                    const strengthB = strengthMap[b.password_strength] || 0;
                    return strengthB - strengthA; // Sort from strongest to weakest
                });
            default:
                return sorted;
        }
    };

    // Filter and sort card details
    const getFilteredAndSortedCards = (): CardDetail[] => {
        let filtered = [...filteredCardDetails];

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(card => !card.is_owner);
        }

        // Apply sorting
        return getSortedCards(filtered);
    };

    // Sort card details
    const getSortedCards = (cards: CardDetail[]): CardDetail[] => {
        if (!cards.length) return cards;
        let sorted = [...cards];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA;
                });
            case 'alphabetical':
                return sorted.sort((a, b) => {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    return nameA.localeCompare(nameB);
                });
            default:
                return sorted;
        }
    };

    // Filter and sort bank details
    const getFilteredAndSortedBanks = (): BankDetail[] => {
        let filtered = [...filteredBankDetails];

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(bank => !bank.is_owner);
        }

        // Apply sorting
        return getSortedBanks(filtered);
    };

    // Sort bank details
    const getSortedBanks = (banks: BankDetail[]): BankDetail[] => {
        if (!banks.length) return banks;
        let sorted = [...banks];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA;
                });
            case 'alphabetical':
                return sorted.sort((a, b) => {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    return nameA.localeCompare(nameB);
                });
            default:
                return sorted;
        }
    };

    // Filter and sort personal details
    const getFilteredAndSortedPersonals = (): PersonalDetail[] => {
        let filtered = [...filteredPersonalDetails];

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(personal => !personal.is_owner);
        }

        // Apply sorting
        return getSortedPersonals(filtered);
    };

    // Sort personal details
    const getSortedPersonals = (personals: PersonalDetail[]): PersonalDetail[] => {
        if (!personals.length) return personals;
        let sorted = [...personals];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA;
                });
            case 'alphabetical':
                return sorted.sort((a, b) => {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    return nameA.localeCompare(nameB);
                });
            default:
                return sorted;
        }
    };

    // Get color for password strength label
    const getStrengthColor = (strength: string): string => {
        switch (strength) {
            case 'Very Weak': return 'text-red-500 bg-red-100';
            case 'Weak': return 'text-orange-500 bg-orange-100';
            case 'Fair': return 'text-yellow-500 bg-yellow-100';
            case 'Strong': return 'text-blue-500 bg-blue-100';
            case 'Very Strong': return 'text-green-500 bg-green-100';
            default: return 'text-gray-500 bg-gray-100';
        }
    };

    // Apply filter settings from temporary state to active state
    const applyFilters = () => {
        setPasswordStrengthFilter(tempPasswordStrengthFilter);
        setShowSharedOnly(tempShowSharedOnly);
        setIsFilterMenuOpen(false);
    };

    // Reset temporary filter settings
    const resetFilters = () => {
        setTempPasswordStrengthFilter('all');
        setTempShowSharedOnly(false);
    };

    // Reset all active filters
    const clearAllFilters = () => {
        setPasswordStrengthFilter('all');
        setShowSharedOnly(false);
    };

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Close sort menu if clicking outside
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setIsSortMenuOpen(false);
            }

            // Close filter menu if clicking outside
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
                setIsFilterMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Sort menu component
    const SortMenu = () => {
        return (
            <div className="relative z-50" ref={sortMenuRef}>
                <button
                    onClick={() => {
                        console.log('Sort button clicked, current state:', isSortMenuOpen);
                        setIsSortMenuOpen(!isSortMenuOpen);
                        setIsFilterMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-2"
                >
                    <ArrowsUpDownIcon className="h-5 w-5 mr-1 text-gray-500" />
                    Sort
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isSortMenuOpen && (
                    <div className="absolute mt-2 right-0 w-64 bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium">Sort by</span>
                        </div>

                        {/* Sort Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <button
                                    className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === 'newest' ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Newest sort clicked');
                                        setSortOption('newest');
                                        setIsSortMenuOpen(false);
                                    }}
                                >
                                    <span>Newest</span>
                                    {sortOption === 'newest' && <ArrowDownIcon className="h-4 w-4" />}
                                </button>
                                <button
                                    className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === 'alphabetical' ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Alphabetical sort clicked');
                                        setSortOption('alphabetical');
                                        setIsSortMenuOpen(false);
                                    }}
                                >
                                    <span>Alphabetical (A-Z)</span>
                                    {sortOption === 'alphabetical' && <ArrowUpIcon className="h-4 w-4" />}
                                </button>
                                {activeTab === 'passwords' && (
                                    <button
                                        className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === 'passwordStrength' ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            console.log('Password strength sort clicked');
                                            setSortOption('passwordStrength');
                                            setIsSortMenuOpen(false);
                                        }}
                                    >
                                        <span>Password Strength</span>
                                        {sortOption === 'passwordStrength' && <ArrowDownIcon className="h-4 w-4" />}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Filter menu component
    const FilterMenu = () => {
        // Get the current tab's label
        const getTabLabel = () => {
            switch (activeTab) {
                case 'passwords': return 'passwords';
                case 'cardDetails': return 'cards';
                case 'bankDetails': return 'bank accounts';
                case 'personalDetails': return 'personal details';
                default: return 'items';
            }
        };

        return (
            <div className="relative z-50" ref={filterMenuRef}>
                <button
                    onClick={() => {
                        console.log('Filter button clicked, current state:', isFilterMenuOpen);
                        setIsFilterMenuOpen(!isFilterMenuOpen);
                        setIsSortMenuOpen(false);
                        // Initialize temporary filter state with current filter values
                        setTempPasswordStrengthFilter(passwordStrengthFilter);
                        setTempShowSharedOnly(showSharedOnly);
                    }}
                    className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <FunnelIcon className="h-5 w-5 mr-1 text-gray-500" />
                    Filter
                    {(passwordStrengthFilter !== 'all' || showSharedOnly) && (
                        <span className="ml-1 px-1.5 py-0.5 bg-primary-100 text-primary-800 text-xs rounded-full">
                            {(passwordStrengthFilter !== 'all' && showSharedOnly) ? '2' : '1'}
                        </span>
                    )}
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isFilterMenuOpen && (
                    <div className="absolute mt-2 right-0 w-72 bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium">Filter {getTabLabel()}</span>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Reset filters clicked');
                                    resetFilters();
                                }}
                                className="text-sm text-primary-600 hover:text-primary-800"
                            >
                                Reset
                            </button>
                        </div>

                        {/* Filter Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <input
                                        id="sharedOnly"
                                        type="checkbox"
                                        checked={tempShowSharedOnly}
                                        onChange={(e) => setTempShowSharedOnly(e.target.checked)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="sharedOnly" className="ml-2 block text-sm text-gray-700">
                                        Shared with me only
                                    </label>
                                </div>

                                {/* Password Strength Filter - Only show for Passwords tab */}
                                {activeTab === 'passwords' && (
                                    <div className="mt-3">
                                        <h4 className="text-xs font-medium text-gray-500 mb-2">Password Strength</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'all' ? 'bg-primary-50 text-primary-700 border border-primary-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('All strengths clicked');
                                                    setTempPasswordStrengthFilter('all');
                                                }}
                                            >
                                                All Strengths
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'veryWeak' ? 'border border-red-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'veryWeak' ? 'Very Weak' : '')}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('Very Weak clicked');
                                                    setTempPasswordStrengthFilter('veryWeak');
                                                }}
                                            >
                                                Very Weak
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'weak' ? 'border border-orange-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'weak' ? 'Weak' : '')}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('Weak clicked');
                                                    setTempPasswordStrengthFilter('weak');
                                                }}
                                            >
                                                Weak
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'fair' ? 'border border-yellow-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'fair' ? 'Fair' : '')}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('Fair clicked');
                                                    setTempPasswordStrengthFilter('fair');
                                                }}
                                            >
                                                Fair
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'strong' ? 'border border-blue-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'strong' ? 'Strong' : '')}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('Strong clicked');
                                                    setTempPasswordStrengthFilter('strong');
                                                }}
                                            >
                                                Strong
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'veryStrong' ? 'border border-green-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'veryStrong' ? 'Very Strong' : '')}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('Very Strong clicked');
                                                    setTempPasswordStrengthFilter('veryStrong');
                                                }}
                                            >
                                                Very Strong
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-3 border-t border-gray-200">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Apply filters clicked');
                                    applyFilters();
                                }}
                                className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="h-screen flex overflow-hidden bg-gray-100">
            <VaultSidebar />
            <div className="flex flex-col flex-1 overflow-hidden">
                <VaultHeader />
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    {/* Hero Banner Section */}
                    <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                            <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                            <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                        </div>
                        
                        <div className="relative px-6 py-12">
                            <div className="max-w-7xl mx-auto">
                                {/* Header Content */}
                                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                                    <div className="mb-6 lg:mb-0">
                                        <h1 className="text-4xl lg:text-5xl font-extrabold text-white mb-4 text-base">
                                            All Items
                                        </h1>
                                        <p className='text-xl text-base text-white/90 max-w-2xl '>
                                            Manage all your digital assets in one secure, encrypted location. 
                                            Access passwords, cards, bank details, and personal information with confidence.
                                        </p>
                                    </div>
                                    
                                    {/* Action Controls */}
                                    <div className="flex items-center space-x-4">
                                        <div className="hidden lg:flex space-x-3 relative z-40">
                                            {/* Commented out - moved to PasswordList component */}
                                            {/* <SortMenu />
                                            <FilterMenu /> */}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Stats Cards */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {itemCounts.passwords}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Passwords
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {itemCounts.cards}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Cards
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {itemCounts.banks}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Banks
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {itemCounts.personals}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Personal
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Modern Tab Navigation */}
                                <div className="flex items-center justify-center px-4 sm:px-0">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-1 sm:p-2 border border-white/30 w-full sm:w-auto sm:max-w-none overflow-x-auto">
                                        <nav className="flex space-x-1 sm:space-x-2 min-w-max sm:min-w-0 sm:justify-center">
                                            <button
                                                onClick={() => handleTabChange('passwords')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'passwords'
                                                        ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                    </svg>
                                                    <span className="hidden sm:inline">Passwords</span>
                                                    <span className="sm:hidden">Pass</span>
                                                </div>
                                                {activeTab === 'passwords' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                            
                                            
                                            <button
                                                onClick={() => handleTabChange('cardDetails')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'cardDetails'
                                                        ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                    </svg>
                                                    <span>Cards</span>
                                                </div>
                                                {activeTab === 'cardDetails' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                            
                                            <button
                                                onClick={() => handleTabChange('bankDetails')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'bankDetails'
                                                        ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                    </svg>
                                                    <span className="hidden sm:inline">Bank Accounts</span>
                                                    <span className="sm:hidden">Banks</span>
                                                </div>
                                                {activeTab === 'bankDetails' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                            
                                            <button
                                                onClick={() => handleTabChange('personalDetails')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'personalDetails'
                                                        ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <span className="hidden sm:inline">Personal Info</span>
                                                    <span className="sm:hidden">Personal</span>
                                                </div>
                                                {activeTab === 'personalDetails' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="container mx-auto px-6 py-8">
                        {/* Mobile Sort/Filter Controls */}
                        <div className="lg:hidden mb-6 flex justify-end space-x-2 relative z-40">
                            {/* Commented out - moved to PasswordList component */}
                            {/* <SortMenu />
                            <FilterMenu /> */}
                        </div>
                        {/* Tab Content */}
                        <div className="space-y-6">
                            {/* Passwords Tab */}
                            {activeTab === 'passwords' && (
                                <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                    {loadingPasswords ? (
                                        <div className="flex justify-center items-center py-12">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                                        </div>
                                    ) : (
                                        <PasswordList
                                            entries={entries}
                                            sortOption={sortOption}
                                            passwordStrengthFilter={passwordStrengthFilter}
                                            showSharedOnly={showSharedOnly}
                                            activeTab={activeTab}
                                            onSuccessfulUpdate={refreshItemCounts}
                                        />
                                    )}
                                </div>
                            )}

            {/* Card Details Tab */}
            {activeTab === 'cardDetails' && (
                <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                   
                    {loadingCards ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : (
                        <>
                            <CardDetailsList
                                details={getFilteredAndSortedCards()}
                                onUpdate={fetchCardDetailsData}
                                onEdit={(card) => {
                                    setSelectedCard(card);
                                    setIsCardModalOpen(true);
                                }}
                            />
                        </>
                    )}
                </div>
            )}

            {/* Bank Details Tab */}
            {activeTab === 'bankDetails' && (
                <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    
                    {loadingBanks ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : (
                        <>
                            <BankDetailsList
                                details={getFilteredAndSortedBanks()}
                                onUpdate={fetchBankDetailsData}
                                onEdit={(bank) => {
                                    setSelectedBank(bank);
                                    setIsBankModalOpen(true);
                                }}
                            />
                        </>
                    )}
                </div>
            )}

            {/* Personal Details Tab */}
            {activeTab === 'personalDetails' && (
                <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    {loadingPersonals ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : (
                        <>
                            <PersonalDetailsList
                                details={getFilteredAndSortedPersonals()}
                                onUpdate={fetchPersonalDetailsData}
                                onEdit={(personal) => {
                                    setSelectedPersonal(personal);
                                    setIsPersonalModalOpen(true);
                                }}
                            />
                        </>
                    )}
                </div>
            )}
                        </div>
                    </div>
                </main>

                <button
                    onClick={() => setIsItemModalOpen(true)}
                    className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 sm:p-4 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center justify-center z-10"
                    aria-label="Add Item"
                >
                    <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="ml-2 hidden sm:inline">Add Item</span>
                </button>
            </div>

            {/* Modals */}
            <AddItemModal
                isOpen={isItemModalOpen}
                onClose={() => setIsItemModalOpen(false)}
                onSelect={handleItemSelect}
            />
           
            <CardDetailsModal
                isOpen={isCardModalOpen}
                onClose={() => setIsCardModalOpen(false)}
                onSuccess={fetchCardDetailsData}
                card={selectedCard || undefined}
                isSharedCard={selectedCard?.is_shared || false}
                permissionLevel={selectedCard?.permission_level || 'read'}
                sharedKey={selectedCard?.shared_key || ''}
            />
            <BankDetailsModal
                isOpen={isBankModalOpen}
                onClose={() => setIsBankModalOpen(false)}
                onSuccess={fetchBankDetailsData}
                bank={selectedBank || undefined}
                isSharedBank={selectedBank?.is_shared || false}
                permissionLevel={selectedBank?.permission_level || 'read'}
                sharedKey={selectedBank?.shared_key || ''}
            />
            <PersonalDetailsModal
                isOpen={isPersonalModalOpen}
                onClose={() => setIsPersonalModalOpen(false)}
                onSuccess={fetchPersonalDetailsData}
                personal={selectedPersonal || undefined}
                isSharedPersonal={selectedPersonal?.is_shared || false}
                permissionLevel={selectedPersonal?.permission_level || 'read'}
                sharedKey={selectedPersonal?.shared_key || ''}
            />
            <PasswordModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={handleSuccess}
            />
            
            {/* RSA Migration Modal - Shows 2 seconds after landing on this page if migration is pending */}
            <RSAMigrationModalTrigger />
        </div>
    );
};

export default VaultPage;